import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface FloristTemplateProps {
    profile: PublicProfile
}

export function FloristTemplate({ profile }: FloristTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Hero Section */}
            <section className="relative min-h-screen overflow-hidden" style={{ backgroundColor: colors.colors.background }}>
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute top-20 right-20 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: colors.colors.accent + '4D' }} />
                    <div className="absolute bottom-20 left-20 w-80 h-80 rounded-full blur-3xl" style={{ backgroundColor: colors.colors.secondary + '4D' }} />
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 py-20 md:px-6 md:py-24 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
                        <div>
                            {profile.tagline && (
                                <p
                                    className="uppercase tracking-widest mb-4 text-sm font-medium"
                                    style={{ color: colors.colors.primary }}
                                >
                                    {profile.tagline}
                                </p>
                            )}
                            <h1
                                className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                Bringing <span className="italic" style={{ color: colors.colors.accent }}>Nature's Beauty</span> to Your Special Moments
                            </h1>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {profile.bio || t('template.florist.customFloralArrangements')}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <button
                                    onClick={() => setIsBookingOpen(true)}
                                    className="inline-flex items-center px-8 py-4 rounded-full font-medium shadow-lg transition-all hover:opacity-90"
                                    style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                >
                                    {t('template.florist.orderFlowers')}
                                </button>
                                <a
                                    href="#services"
                                    className="inline-flex items-center px-8 py-4 border-2 rounded-full font-medium transition-all hover:opacity-90"
                                    style={{ borderColor: colors.colors.text, color: colors.colors.text }}
                                >
                                    {t('template.florist.viewServices')}
                                </a>
                            </div>
                            {profile.location && (
                                <p className="text-sm mt-6" style={{ color: colors.colors.textMuted }}>
                                    📍 {profile.location}
                                </p>
                            )}
                        </div>
                        <div className="relative">
                            <div
                                className="absolute inset-0 rounded-3xl transform rotate-3"
                                style={{ backgroundImage: `linear-gradient(135deg, ${colors.colors.accent}33, ${colors.colors.secondary}33)` }}
                            />
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="relative rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="relative rounded-3xl shadow-2xl w-full aspect-square flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary }}
                                >
                                    <span className="text-6xl">💐</span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6"
                            >
                                <div className="flex items-center space-x-3">
                                    <span style={{ color: colors.colors.primary }}>🚚</span>
                                    <div>
                                        <p className="font-semibold" style={{ color: colors.colors.text }}>{t('template.florist.sameDayDelivery')}</p>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.florist.orderBy2Pm')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="relative">
                            {profile.profile_image ? (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-3xl shadow-2xl w-full object-cover"
                                />
                            ) : (
                                <div
                                    className="rounded-3xl shadow-2xl w-full aspect-square flex items-center justify-center"
                                    style={{ backgroundColor: colors.colors.secondary }}
                                >
                                    <span className="text-6xl">💐</span>
                                </div>
                            )}
                            <div
                                className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.background }}
                            >
                                <span className="text-2xl">❤️</span>
                                <p className="font-semibold">{t('template.florist.madeWithLove')}</p>
                            </div>
                        </div>
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.primary }}
                            >
                                {t('template.florist.aboutUs')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                Where Artistry Meets <span className="italic" style={{ color: colors.colors.accent }}>Nature</span>
                            </h2>
                            {profile.bio && (
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                            )}
                            <div className="grid grid-cols-2 gap-6">
                                <div className="rounded-xl p-5" style={{ backgroundColor: colors.colors.secondary + '80' }}>
                                    <span style={{ color: colors.colors.primary }}>🌿</span>
                                    <h4 className="font-semibold mt-3" style={{ color: colors.colors.text }}>{t('template.florist.localSourcing')}</h4>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.florist.freshFromLocalFarms')}</p>
                                </div>
                                <div className="rounded-xl p-5" style={{ backgroundColor: colors.colors.secondary + '80' }}>
                                    <span style={{ color: colors.colors.primary }}>🏆</span>
                                    <h4 className="font-semibold mt-3" style={{ color: colors.colors.text }}>{t('template.florist.awardWinning')}</h4>
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>{t('template.florist.bestFlorist2023')}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            {profile.services && profile.services.length > 0 && (
                <section id="services" className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.primary }}
                            >
                                {t('template.florist.whatWeOffer')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-semibold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.florist.servicesAndPricing')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {profile.services.map((service, index) => (
                                <div
                                    key={service.id}
                                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow"
                                >
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
                                        style={{ backgroundColor: colors.colors.secondary + '80' }}
                                    >
                                        <span style={{ color: colors.colors.accent }}>
                                            {index === 0 ? '💼' : index === 1 ? '💕' : index === 2 ? '🏢' : index === 3 ? '📦' : index === 4 ? '🧴' : '🎁'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-start mb-4">
                                        <h3
                                            className="text-2xl font-semibold"
                                            style={{ fontFamily: typography.heading, color: colors.colors.text }}
                                        >
                                            {service.name}
                                        </h3>
                                        {service.price_display !== 'hidden' && service.price && (
                                            <span className="text-2xl font-semibold" style={{ color: colors.colors.accent }}>
                                                ${service.price.toFixed(0)}+
                                            </span>
                                        )}
                                    </div>
                                    {service.description && (
                                        <p className="mb-4" style={{ color: colors.colors.textMuted }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center text-sm" style={{ color: colors.colors.primary }}>
                                            <span className="mr-2">⏱</span>
                                            <span>{service.duration_minutes} min</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Gallery Section */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="gallery" className="py-16 md:py-24 px-4 md:px-6 lg:px-8" style={{ backgroundColor: colors.colors.secondary + '40' }}>
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.primary }}
                            >
                                {t('template.florist.gallery')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-semibold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.florist.ourWork')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={8}
                            enableLightbox
                            showCaptions
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Availability Section */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-16 md:py-24 px-4 md:px-6 lg:px-8 bg-white">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.primary }}
                            >
                                {t('template.florist.visitUs')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-semibold"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.florist.storeHours')}
                            </h2>
                        </div>
                        <div className="rounded-3xl p-8 md:p-12 shadow-lg" style={{ backgroundColor: colors.colors.background }}>
                            <div className="grid md:grid-cols-2 gap-8">
                                {profile.availability.slice(0, 7).map((day) => (
                                    <div
                                        key={day.day_of_week}
                                        className="flex justify-between items-center py-3 border-b"
                                        style={{ borderColor: colors.colors.accent + '33' }}
                                    >
                                        <span className="font-semibold" style={{ color: colors.colors.text }}>
                                            {day.day_name}
                                        </span>
                                        <span style={{ color: colors.colors.textMuted }}>
                                            {day.is_available
                                                ? day.is_all_day
                                                    ? t('template.common.allDay')
                                                    : `${day.start_time} - ${day.end_time}`
                                                : t('template.common.closed')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8 text-center rounded-xl p-6" style={{ backgroundColor: colors.colors.accent + '1A' }}>
                                <p style={{ color: colors.colors.text }}>
                                    <span className="mr-2" style={{ color: colors.colors.accent }}>🚚</span>
                                    <strong>{t('template.florist.deliveryHours')}:</strong> {t('template.florist.monSat10To5')} | {t('template.florist.sameDayOrdersDeadline')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section id="contact" className="py-16 md:py-24 px-4 md:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm font-medium"
                                style={{ color: colors.colors.primary }}
                            >
                                {t('template.florist.getInTouch')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl lg:text-5xl font-semibold mb-6"
                                style={{ fontFamily: typography.heading, color: colors.colors.text }}
                            >
                                {t('template.florist.letsCreateSomethingBeautiful')}
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {t('template.florist.whetherYouNeedSimpleBouquet')}
                            </p>
                            <div className="space-y-6">
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary + '80' }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.florist.visitOurShop')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary + '80' }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.florist.callUs')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: colors.colors.secondary + '80' }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold" style={{ color: colors.colors.text }}>{t('template.florist.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-semibold mb-4" style={{ color: colors.colors.text }}>{t('template.florist.followUs')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all hover:opacity-80"
                                                style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                            >
                                                <span className="text-lg">
                                                    {link.platform === 'instagram' && '📸'}
                                                    {link.platform === 'facebook' && '📘'}
                                                    {link.platform === 'pinterest' && '📌'}
                                                    {!['instagram', 'facebook', 'pinterest'].includes(link.platform) && '🔗'}
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <form className="bg-white rounded-3xl p-8 shadow-xl">
                                <h3
                                    className="text-2xl font-semibold mb-6"
                                    style={{ fontFamily: typography.heading, color: colors.colors.text }}
                                >
                                    {t('template.florist.orderOrInquiry')}
                                </h3>
                                <div className="space-y-5">
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.florist.yourName')}
                                        </label>
                                        <input
                                            type="text"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.accent + '33',
                                            }}
                                            placeholder={t('template.florist.yourName')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.florist.email')}
                                        </label>
                                        <input
                                            type="email"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.accent + '33',
                                            }}
                                            placeholder="your@email.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.florist.phone')}
                                        </label>
                                        <input
                                            type="tel"
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.accent + '33',
                                            }}
                                            placeholder="(555) 123-4567"
                                        />
                                    </div>
                                    {profile.services && profile.services.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                                {t('template.florist.serviceNeeded')}
                                            </label>
                                            <select
                                                className="w-full px-4 py-4 rounded-xl border outline-none transition-all cursor-pointer"
                                                style={{
                                                    backgroundColor: colors.colors.background,
                                                    borderColor: colors.colors.accent + '33',
                                                }}
                                            >
                                                <option value="">{t('template.florist.selectAService')}</option>
                                                {profile.services.map((service) => (
                                                    <option key={service.id} value={service.id}>
                                                        {service.name}
                                                        {service.price && ` - $${service.price.toFixed(0)}+`}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-2" style={{ color: colors.colors.text }}>
                                            {t('template.florist.message')}
                                        </label>
                                        <textarea
                                            rows={4}
                                            className="w-full px-4 py-4 rounded-xl border outline-none transition-all resize-none"
                                            style={{
                                                backgroundColor: colors.colors.background,
                                                borderColor: colors.colors.accent + '33',
                                            }}
                                            placeholder="Tell us about your needs..."
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="w-full py-4 rounded-xl font-medium text-center transition-colors hover:opacity-90"
                                        style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}
                                    >
                                        {t('template.florist.sendMessage')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 md:px-6 lg:px-8 text-center" style={{ backgroundColor: colors.colors.text }}>
                <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundImage: `linear-gradient(135deg, ${colors.colors.accent}, ${colors.colors.secondary})` }}
                >
                    <span className="text-2xl">💐</span>
                </div>
                <h3
                    className="text-2xl font-semibold mb-2"
                    style={{ fontFamily: typography.heading, color: colors.colors.background }}
                >
                    {profile.business_name}
                </h3>
                <p className="mb-6" style={{ color: colors.colors.textMuted }}>
                    {t('template.florist.floristAndEventDesign')}
                </p>
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Follow on ${link.platform}`}
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                style={{ backgroundColor: colors.colors.background + '1A', color: colors.colors.background }}
                            >
                                <span className="text-sm">
                                    {link.platform === 'instagram' && '📸'}
                                    {link.platform === 'facebook' && '📘'}
                                    {link.platform === 'pinterest' && '📌'}
                                    {!['instagram', 'facebook', 'pinterest'].includes(link.platform) && '🔗'}
                                </span>
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.florist.allRightsReserved')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default FloristTemplate;
